import { useState, useEffect } from "react";
import { X, ExternalLink, Heart } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fixImgUrl(url) {
  if (!url) return "";
  if (url.includes("/api/uploads/")) {
    const filename = url.split("/api/uploads/").pop();
    return `/api/uploads/${filename}`;
  }
  if (url.startsWith("http://") && typeof window !== "undefined" && window.location.protocol === "https:") {
    return url.replace("http://", "https://");
  }
  return url;
}

export default function PromotionPopup() {
  const [promo, setPromo] = useState(null);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const shown = sessionStorage.getItem("promo_dismissed");
    if (shown) return;

    axios.get(`${API}/promotions/popup`).then(res => {
      if (res.data && res.data.id) {
        setPromo(res.data);
        setTimeout(() => setVisible(true), 1500);
        setTimeout(() => {
          setVisible(false);
          sessionStorage.setItem("promo_dismissed", "1");
        }, (res.data.popup_duration || 8) * 1000 + 1500);
      }
    }).catch(() => {});
  }, []);

  const dismiss = () => {
    setVisible(false);
    setDismissed(true);
    sessionStorage.setItem("promo_dismissed", "1");
  };

  if (dismissed || !promo) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 60, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 60, scale: 0.95 }}
          transition={{ duration: 0.4 }}
          className="fixed bottom-20 right-4 sm:right-6 z-[60] w-[340px] max-w-[calc(100vw-2rem)] border border-white/15 bg-zinc-950/95 backdrop-blur-xl shadow-2xl"
          data-testid="promotion-popup"
        >
          {/* Close Button */}
          <button
            onClick={dismiss}
            data-testid="popup-close-btn"
            className="absolute top-3 right-3 z-10 w-7 h-7 flex items-center justify-center text-zinc-500 hover:text-white transition-colors"
          >
            <X size={16} />
          </button>

          {/* Image */}
          {promo.image_url && (
            <div className="w-full overflow-hidden">
              <img
                src={fixImgUrl(promo.image_url)}
                alt={promo.title}
                className="w-full object-contain max-h-[200px]"
              />
            </div>
          )}

          {/* Content */}
          <div className="p-5">
            <p className="font-body text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Upcoming Event</p>
            <h3 className="font-subheading text-lg uppercase tracking-wider mb-2 pr-6">{promo.title}</h3>
            <p className="font-body text-xs text-zinc-400 mb-1">{promo.event_date}</p>
            <p className="font-body text-sm text-zinc-500 line-clamp-2 mb-4">{promo.description}</p>

            <div className="flex gap-3">
              {promo.support_url && (
                <a
                  href={promo.support_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="popup-support-btn"
                  className="flex-1 flex items-center justify-center gap-2 font-subheading text-xs uppercase tracking-widest bg-white text-black px-4 py-2.5 hover:bg-zinc-200 transition-colors"
                >
                  <Heart size={12} /> Support
                </a>
              )}
              <a
                href={promo.support_url || "/get-involved"}
                target={promo.support_url ? "_blank" : "_self"}
                rel="noopener noreferrer"
                data-testid="popup-info-btn"
                className="flex-1 flex items-center justify-center gap-2 font-subheading text-xs uppercase tracking-widest border border-white/20 text-white px-4 py-2.5 hover:border-white hover:bg-white/5 transition-all"
              >
                <ExternalLink size={12} /> Details
              </a>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
